import { useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { useMarkInboxUnread } from "@/hooks/useInbox";

/**
 * Put this conversation back in the Buzón, then leave it.
 *
 * The Buzón is shared by the whole team, so reading a conversation a colleague is
 * handling costs everyone their unread list. This is the way back in — team-wide,
 * like every other inbox marker, and cleared again the next time anyone opens the
 * lead.
 *
 * Navigating away is part of the action, not a convenience. Opening a lead marks
 * it read, so a mark left on screen is undone by the next person who opens the
 * lead — or by this one on a refresh — and reads as a button that did nothing.
 * Leaving is what makes it stick. It goes back to wherever the user came from
 * (`backPath`), which for anyone triaging is the Buzón itself.
 *
 * Navigation is in `onSuccess`, never beside the mutate call: a failed request
 * must leave the user on the conversation, looking at the state they thought they
 * had changed.
 */
export function MarkUnreadButton({
  leadId,
  backPath,
  compact = false,
}: {
  leadId: string;
  backPath: string;
  /** Icon-only, for the mobile header where there is no room for a label. */
  compact?: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const { mutate, isPending } = useMarkInboxUnread();
  const markUnread = (): void =>
    mutate(leadId, { onSuccess: () => navigate(backPath) });

  if (compact) {
    return (
      <button
        type="button"
        onClick={markUnread}
        disabled={isPending}
        className="shrink-0 rounded-lg p-2 text-ink-400 transition-colors hover:bg-ink-050 hover:text-ink-700 disabled:opacity-50"
        aria-label="Marcar como no leído"
      >
        <Mail size={18} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={markUnread}
      disabled={isPending}
      className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-600 transition-colors hover:bg-ink-050 disabled:opacity-50"
      aria-label="Marcar como no leído"
    >
      <Mail size={14} />
      No leído
    </button>
  );
}
